import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { heureka } from "@otto-ec/heureka";
import type { Appendable } from "../util/Appendable";
import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";
import { dereference, readLabel } from "../tracking/FeatureTracking";
import { isSelected, SvelteFacetValue, updateFacetValueTagPosition } from "../filter/SvelteFacetValue";
import { clone, element } from "../util/Utils";
import { setChangeLabels } from "../filter/FilterTracking";
import { events } from "../events";
import { RefinementBar } from "../refinementBar/RefinementBar";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import FilterSwitchBanner from "../filterBanner/FilterSwitchBanner";
import { syncRefinementBarCategoryPath } from "../categorypath/categoryPath";

const CLASS_NEXT_BEST_ACTION_WRAPPER = "hc_facetValueTags";
const CLASS_DESELECT_ALL = "hc_deselectAll";
const SELECTOR_DESELECT_ALL = `.${CLASS_DESELECT_ALL}`;
const SELECTOR_TEMPLATE_FACET_VALUE_TAGS = `.hc_svelteFacetValueTagsWrapper .${CLASS_NEXT_BEST_ACTION_WRAPPER}`;

export class FacetValueTagsComponent implements Appendable {
  static readonly factory = HeurekaElementFactory.byClass(CLASS_NEXT_BEST_ACTION_WRAPPER, FacetValueTagsComponent);

  /*               */

  constructor(readonly tags: HTMLElement) {}

  /*                  */

  static template(rootElement?: ParentNode | null) {
    return FacetValueTagsComponent.factory.pick(SELECTOR_TEMPLATE_FACET_VALUE_TAGS, rootElement);
  }

  /*               */

  init() {
    const filterSwitchBanner = FilterSwitchBanner.fromTemplate();
    syncRefinementBarCategoryPath(this.values);
    this.registerListener();
    this.deselectAllButton?.addEventListener("click", () => {
      /*                                                                                         */
      this.selectedValues.forEach((tag) => {
        tag.updateTrackingLabelsOnReferenced("deleted", "x_all_top", "list_initial");
        filterSwitchBanner?.deleted("x_all_top");
      });
    });
    this.selectedValues.forEach((tag) => {
      tag.facetValue.addEventListener("click", () => {
        /*                                                                                   */
        tag.updateTrackingLabelsOnReferenced("deleted", "x_single_top", "list_initial");
        if (tag.facetValue.value === filterSwitchBanner?.bannerSwitch?.value) {
          filterSwitchBanner?.deleted("x_single_top");
        }
      });
    });
    return this;
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    FacetValueTagsComponent.emitSelectedFacetValues(event, rootElement);
  }

  private static emitSelectedFacetValues(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    const refinementBar = RefinementBar.facetValueTagsElement(rootElement);
    if (refinementBar) {
      const facetValueTags = FacetValueTagsComponent.factory.pick(undefined, refinementBar);
      const selectedValues = facetValueTags?.selectedValues.map((value) => value.title);
      if (selectedValues && selectedValues.length) {
        events.facetValueTagsLoaded(event.rule, selectedValues);
      }
    }
  }

  /*                       */

  clone() {
    return FacetValueTagsComponent.factory.create(clone(this.tags));
  }

  get deselectAllButton(): HTMLElement | null {
    return element(SELECTOR_DESELECT_ALL, this.tags);
  }

  get values(): SvelteFacetValue[] {
    return SvelteFacetValue.factory.all(this.tags);
  }

  get selectedValues(): SvelteFacetValue[] {
    return this.values.filter(isSelected);
  }

  get deselectAll(): HTMLFormElement | undefined {
    return this.tags.querySelector(SELECTOR_DESELECT_ALL);
  }

  appendTo(elem: HTMLElement): void {
    elem.appendChild(this.tags);
    trackFacetValueTags();
  }

  removeNonSelectedNbaWithRef(ref: string): void {
    const value = this.values.find((value) => value.ref === ref && !value.checked);
    value?.liElem?.remove();
  }

  /*                 */

  private handleChange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  registerListener() {
    this.tags.addEventListener("change", this.handleChange);
    this.deselectAll?.addEventListener("submit", (event) => {
      this.values.forEach((value) => {
        const referenced = dereference(value.facetValue);
        clearFormInput(referenced);
      });
      interceptSubmit(event);
    });
  }
}

function trackFacetValueTags() {
  const facetValueTagsTemplate = FacetValueTagsComponent.template();
  if (facetValueTagsTemplate) {
    facetValueTagsTemplate.values.forEach(updateFacetValueTagPosition);
  }
}
